import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface ActivityRecord {
  _id?: string;
  userId?: string;
  date: string;
  clockInTime?: string | null;
  clockOutTime?: string | null;
  totalActiveMs: number;
  totalIdleMs: number;
  lastEventTime?: string;
}

interface ActivityState {
  current: ActivityRecord | null;
  loading: boolean;
  error: string | null;

  fetchDailyReport: (date?: string) => Promise<void>;
  startTracking: () => Promise<void>;
  stopTracking: () => Promise<void>;
  recordActivity: (activeMs: number, idleMs: number) => Promise<void>;
  reset: () => void;
}

const todayDate = (): string => {
  const parts = new Date().toISOString().split("T");
  return parts[0] ?? "";
};

const makeEmptyRecord = (date: string = todayDate()): ActivityRecord => ({
  date,
  totalActiveMs: 0,
  totalIdleMs: 0,
});

const normalizeServerRecord = (
  data: Partial<ActivityRecord> | null | undefined,
  dateFallback: string = todayDate()
): ActivityRecord => {
  const finalDate: string =
    typeof data?.date === "string" && data.date.length > 0
      ? data.date
      : dateFallback;

  return {
    ...(data?._id ? { _id: data._id } : {}),
    ...(data?.userId ? { userId: data.userId } : {}),

    date: finalDate,

    clockInTime: data?.clockInTime ?? null,
    clockOutTime: data?.clockOutTime ?? null,

    totalActiveMs:
      typeof data?.totalActiveMs === "number" ? data.totalActiveMs : 0,
    totalIdleMs:
      typeof data?.totalIdleMs === "number" ? data.totalIdleMs : 0,

    ...(data?.lastEventTime ? { lastEventTime: data.lastEventTime } : {}),
  };
};

export const useActivityStore = create<ActivityState>((set, get) => ({
  current: null,
  loading: false,
  error: null,

  fetchDailyReport: async (date: string = todayDate()) => {
    set({ loading: true, error: null });
    try {
      const res = await axiosInstance.get<ActivityRecord | null>(
        `/activity/report/${date}`
      );

      const serverData = res.data ?? null;
      const normalized = normalizeServerRecord(serverData, date);

      set({ current: normalized });
    } catch (err: any) {
      console.error("fetchDailyReport error:", err);
      set({
        error: err?.response?.data?.message || "Failed to fetch report",
        current: makeEmptyRecord(date),
      });
    } finally {
      set({ loading: false });
    }
  },

  startTracking: async () => {
    try {
      await axiosInstance.post("/activity/start");
    } catch (err: any) {
      console.error("startTracking error:", err);
      toast.error("Failed to start tracking");
    }
  },

  stopTracking: async () => {
    try {
      await axiosInstance.post("/activity/stop");
    } catch (err: any) {
      console.error("stopTracking error:", err);
      toast.error("Failed to stop tracking");
    }
  },

  recordActivity: async (activeMs: number, idleMs: number) => {
    const cur = get().current ?? makeEmptyRecord();

    const updated: ActivityRecord = {
      ...cur,
      totalActiveMs: cur.totalActiveMs + activeMs,
      totalIdleMs: cur.totalIdleMs + idleMs,
      lastEventTime: new Date().toISOString(),
    };

    set({ current: updated });

    try {
      await axiosInstance.post("/activity/record", { activeMs, idleMs });
    } catch (err: any) {
      console.error("recordActivity error:", err);
      toast.error("Failed to save activity");
    }
  },

  reset: () => set({ current: makeEmptyRecord(), error: null }),
}));
